import { NODE_DEFAULTS } from './nodeTypes';
import { tidyLayout } from './layoutUtils';

const rid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const wire = (source, handle, target) => ({
  id: `e_${source}_${target}_${handle || 'out'}_${rid()}`, source, sourceHandle: handle || 'out', target, targetHandle: 'in',
});

export const makeQueue = (position) => ({
  id: `n_queue_${rid()}`, type: 'queue', position, data: { ...NODE_DEFAULTS.queue },
});

const dept = (n, data) => ({ ...n, type: 'department', data: { ...NODE_DEFAULTS.department, ...data } });

// Convert retired step types (AI Agent, Transfer, Time Router) into Departments.
function convertLegacy(nodes, edges, phoneNumbers) {
  const agentNum = {};
  (phoneNumbers || []).forEach((p) => { if (p.agent_id) agentNum[p.agent_id] = p.phone_number || p.number; });
  let changed = false;
  let ns = nodes.map((n) => {
    const d = n.data || {};
    if (n.type === 'transfer') {
      changed = true;
      return dept(n, { name: d.label || 'Transfer', ring_seconds: d.ring_timeout || 20, record: !!d.record_call, numbers: [{ number: d.number || '', label: '' }] });
    }
    if (n.type === 'ai_agent') {
      changed = true;
      return dept(n, { name: d.agent_name || 'AI agent', announce: false, numbers: [{ number: agentNum[d.agent_id] || '', label: d.agent_name || 'AI agent', is_agent: true }] });
    }
    return n;
  });
  let es = [...edges];
  ns.filter((n) => n.type === 'time_router').forEach((t) => {
    changed = true;
    const incoming = es.filter((e) => e.target === t.id);
    const openE = es.find((e) => e.source === t.id && e.sourceHandle === 'open');
    const closedE = es.find((e) => e.source === t.id && e.sourceHandle === 'closed');
    es = es.filter((e) => e.source !== t.id && e.target !== t.id);
    const next = openE?.target || closedE?.target;
    if (next) es = es.concat(incoming.map((e) => ({ ...wire(e.source, e.sourceHandle, next) })));
    const open = openE && ns.find((n) => n.id === openE.target);
    if (open?.type === 'department' && !open.data?.hours_enabled) {
      const td = t.data || {};
      ns = ns.map((n) => (n.id === open.id ? { ...n, data: { ...n.data, hours_enabled: true, windows: (td.windows || []).slice(0, 1), timezone: td.timezone || n.data?.timezone } } : n));
      if (closedE && !es.some((e) => e.source === open.id && e.sourceHandle === 'closed')) es.push(wire(open.id, 'closed', closedE.target));
    }
    ns = ns.filter((n) => n.id !== t.id);
  });
  return { nodes: ns, edges: es, changed };
}

// Every Department must be reached through a Hold Queue.
function enforceQueues(nodes, edges) {
  let ns = [...nodes];
  let es = [...edges];
  let added = false;
  const typeOf = (id) => ns.find((n) => n.id === id)?.type;
  nodes.filter((n) => n.type === 'department').forEach((d) => {
    const direct = es.filter((e) => e.target === d.id && typeOf(e.source) !== 'queue');
    if (!direct.length) return;
    let qid = es.find((e) => e.target === d.id && typeOf(e.source) === 'queue')?.source;
    if (!qid) {
      const q = makeQueue({ x: d.position.x, y: d.position.y - 190 });
      ns.push(q);
      es.push(wire(q.id, 'out', d.id));
      qid = q.id;
      added = true;
    }
    const ids = new Set(direct.map((e) => e.id));
    es = es.filter((e) => !ids.has(e.id)).concat(direct.map((e) => wire(e.source, e.sourceHandle, qid)));
  });
  return { nodes: ns, edges: es, changed: es.length !== edges.length || added || es.some((e, i) => e !== edges[i]), added };
}

export function migrateFlow(nodes, edges, phoneNumbers) {
  const a = convertLegacy(nodes || [], edges || [], phoneNumbers);
  const b = enforceQueues(a.nodes, a.edges);
  const changed = a.changed || b.changed;
  return { nodes: b.added || a.changed ? tidyLayout(b.nodes, b.edges) : b.nodes, edges: b.edges, changed };
}
